// Owner: shell-nav (plan §4.2, §9.2). The shell's keyboard commands: ⌘\ collapses and expands the rail;
// pin or unpin the open page; copy its link. They live in the `shell` scope, which the authenticated
// layouts keep active with the rail as context, so the `?` sheet and ⌘K list them with their keys.
// Discovered by glob (`features/**/commands.ts`); keep this file tiny.
import { defineCommand, defineScope } from '@dataroom/interaction';
import { defineAppCommands } from '../../extensions/commands';
import { t } from '../../lib/i18n';

export type ShellCommandContext = {
  railCollapsed: () => boolean;
  toggleRail: () => void;
  /** The open page can be pinned (a record, view, page, deck, document template or dashboard). */
  pinnable: () => boolean;
  pinned: () => boolean;
  togglePin: () => void;
  copyLink: () => void;
};

export const shellScope = defineScope<ShellCommandContext>({
  id: 'shell',
  kind: 'global',
  // The app's global title: the `?` sheet shows one Everywhere section (scopes with one title merge).
  title: () => t('interaction.scope.app'),
});

export const toggleRailCommand = defineCommand<ShellCommandContext>({
  id: 'shell.rail.toggle',
  title: () => t('shell.cmd.toggleRail'),
  icon: 'columns',
  shortcut: 'mod+\\',
  scope: shellScope,
  run: (ctx) => ctx.toggleRail(),
});

export const pinCommand = defineCommand<ShellCommandContext>({
  id: 'shell.pin',
  title: () => t('shell.cmd.pin'),
  icon: 'star',
  scope: shellScope,
  when: (ctx) => ctx.pinnable() && !ctx.pinned(),
  run: (ctx) => ctx.togglePin(),
});

export const unpinCommand = defineCommand<ShellCommandContext>({
  id: 'shell.unpin',
  title: () => t('shell.cmd.unpin'),
  icon: 'star',
  scope: shellScope,
  when: (ctx) => ctx.pinnable() && ctx.pinned(),
  run: (ctx) => ctx.togglePin(),
});

export const copyLinkCommand = defineCommand<ShellCommandContext>({
  id: 'shell.copyLink',
  title: () => t('shell.cmd.copyLink'),
  icon: 'link',
  scope: shellScope,
  run: (ctx) => ctx.copyLink(),
});

export default defineAppCommands({
  id: 'shell',
  scopes: [shellScope],
  commands: [toggleRailCommand, pinCommand, unpinCommand, copyLinkCommand],
});
